import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Index from "./pages/Index";
import { resolveRedirect } from "./components/LegacyRedirect";
const LeadPopup = lazy(() => import("./components/LeadPopup"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));

// Lazy load all non-critical pages for faster initial load
const SearchPage = lazy(() => import("./pages/Search"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const SavedProperties = lazy(() => import("./pages/SavedProperties"));
const Buy = lazy(() => import("./pages/Buy"));
const Sell = lazy(() => import("./pages/Sell"));
const About = lazy(() => import("./pages/About"));
const MarketReports = lazy(() => import("./pages/MarketReports"));
const WashingtonCountyMarketReport = lazy(() => import("./pages/WashingtonCountyMarketReport"));
const Luxury = lazy(() => import("./pages/Luxury"));
const JustListed = lazy(() => import("./pages/JustListed"));
const Under2Million = lazy(() => import("./pages/Under2Million"));
const UpcomingListings = lazy(() => import("./pages/UpcomingListings"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const FairHousing = lazy(() => import("./pages/FairHousing"));
const AccessibilityStatement = lazy(() => import("./pages/AccessibilityStatement"));
const ExclusiveListingDetail = lazy(() => import("./pages/ExclusiveListingDetail"));
const UploadExclusiveListing = lazy(() => import("./pages/UploadExclusiveListing"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PropertyValuation = lazy(() => import("./pages/PropertyValuation"));
const AboutNeil = lazy(() => import("./pages/AboutNeil"));
const Communities = lazy(() => import("./pages/Communities"));
const CommunityPage = lazy(() => import("./pages/CommunityPage"));
const FeaturedListings = lazy(() => import("./pages/FeaturedListings"));
const StGeorgeHomes = lazy(() => import("./pages/StGeorgeHomes"));
const WashingtonUtHomes = lazy(() => import("./pages/WashingtonUtHomes"));
const IvinsHomes = lazy(() => import("./pages/IvinsHomes"));
const HurricaneHomes = lazy(() => import("./pages/HurricaneHomes"));
const SantaClaraHomes = lazy(() => import("./pages/SantaClaraHomes"));
const LaVerkinHomes = lazy(() => import("./pages/LaVerkinHomes"));
const LeedsUtHomes = lazy(() => import("./pages/LeedsUtHomes"));
const ToquervilleHomes = lazy(() => import("./pages/ToquervilleHomes"));
const Listing = lazy(() => import("./pages/Listing"));
const CityPage = lazy(() => import("./pages/CityPage"));
// Old SEO city pages replaced by /communities/[slug]-utah system; redirects handled by LegacyRedirect
const LaVerkinRealEstate = lazy(() => import("./pages/seo/LaVerkinRealEstate"));
const KanabRealEstate = lazy(() => import("./pages/seo/KanabRealEstate"));
const NewHarmonyRealEstate = lazy(() => import("./pages/seo/NewHarmonyRealEstate"));
const CedarCityRealEstate = lazy(() => import("./pages/seo/CedarCityRealEstate"));
const BrianHeadRealEstate = lazy(() => import("./pages/seo/BrianHeadRealEstate"));
const SeniorCommunities = lazy(() => import("./pages/SeniorCommunities"));
const HomeInspectionGuide = lazy(() => import("./pages/HomeInspectionGuide"));
const MortgageProcess = lazy(() => import("./pages/MortgageProcess"));
const NegotiatingTips = lazy(() => import("./pages/NegotiatingTips"));

const queryClient = new QueryClient();

const LegacyRedirectOrNotFound = () => {
  const location = useLocation();
  const target = resolveRedirect(location.pathname);
  if (target) return <Navigate to={target} replace />;
  return <NotFound />;
};

const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter future={{ v7_startTransition: true }}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/login" element={<Auth />} />
            <Route path="/signup" element={<Auth />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/search" element={<SearchResults />} />
            <Route path="/property-search" element={<SearchPage />} />
            <Route path="/saved-properties" element={<SavedProperties />} />
            <Route path="/buy" element={<Buy />} />
            <Route path="/sell" element={<Sell />} />
            <Route path="/about" element={<About />} />
            <Route path="/market-reports" element={<MarketReports />} />
            <Route path="/market-reports/:slug" element={<WashingtonCountyMarketReport />} />
            <Route path="/luxury" element={<Luxury />} />
            <Route path="/55-plus-communities" element={<SeniorCommunities />} />
            <Route path="/just-listed" element={<JustListed />} />
            <Route path="/upcoming-listings" element={<UpcomingListings />} />
            <Route path="/under-2-million" element={<Under2Million />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/fair-housing" element={<FairHousing />} />
            <Route path="/accessibility-statement" element={<AccessibilityStatement />} />
            <Route path="/upload-exclusive-listing" element={<UploadExclusiveListing />} />
            <Route path="/listing/:slug" element={<ExclusiveListingDetail />} />
            <Route path="/propertyvaluation" element={<PropertyValuation />} />
            <Route path="/about-neil" element={<AboutNeil />} />
            <Route path="/communities" element={<Communities />} />
            <Route path="/communities/:slug" element={<CommunityPage />} />
            <Route path="/featured-listings" element={<FeaturedListings />} />
            <Route path="/st-george-homes" element={<StGeorgeHomes />} />
            <Route path="/washington-ut-homes" element={<WashingtonUtHomes />} />
            <Route path="/ivins-homes" element={<IvinsHomes />} />
            <Route path="/hurricane-homes" element={<HurricaneHomes />} />
            <Route path="/santa-clara-homes" element={<SantaClaraHomes />} />
            <Route path="/la-verkin-homes" element={<LaVerkinHomes />} />
            <Route path="/leeds-ut-homes" element={<LeedsUtHomes />} />
            <Route path="/toquerville-homes" element={<ToquervilleHomes />} />
            <Route path="/listing" element={<Listing />} />
            <Route path="/city/:slug" element={<CityPage />} />
            {/* Old SEO city routes (/st-george-real-estate, etc.) now redirect to /communities/[slug]-utah via LegacyRedirect */}
            <Route path="/la-verkin-real-estate" element={<LaVerkinRealEstate />} />
            <Route path="/kanab-real-estate" element={<KanabRealEstate />} />
            <Route path="/new-harmony-real-estate" element={<NewHarmonyRealEstate />} />
            <Route path="/cedar-city-real-estate" element={<CedarCityRealEstate />} />
            <Route path="/brian-head-real-estate" element={<BrianHeadRealEstate />} />
            <Route path="/home-inspection-guide" element={<HomeInspectionGuide />} />
            <Route path="/mortgage-process" element={<MortgageProcess />} />
            <Route path="/negotiating-tips" element={<NegotiatingTips />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<LegacyRedirectOrNotFound />} />
          </Routes>
          <LeadPopup />
        </Suspense>
      </TooltipProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
